<template>
    <div class="title-and-controls">
        <span class="page-title">
            <div class="document-icon" v-html="fileIcon" />
            <EditableTitle v-model="artefactMeta" icon="" />
            <span
                v-if="artefactMeta?.revision"
                class="revision"
                :title="artefactMeta.revision"
            >
                (Old version)
            </span>
        </span>
        <div class="controls">
            <Button
                v-for="button in buttons"
                :key="button.label"
                :class="button.class"
                :icon="button.icon"
                :label="button.label"
                @click="button.onClick"
            />
            <template v-if="artefactMeta">
                <Button
                    severity="secondary"
                    icon="pi pi-ellipsis-v"
                    aria-haspopup="true"
                    aria-controls="document_popup_menu"
                    aria-label="Document Menu"
                    @click="toggleMenu"
                />
                <Menu
                    id="document_popup_menu"
                    ref="menu"
                    :model="contextMenuItems"
                    :popup="true"
                />
                <input
                    name="file"
                    type="file"
                    style="display: none"
                    ref="fileUploadElement"
                    @change="(e) => uploadOverwrite(e as HTMLInputEvent)"
                />
            </template>
        </div>
    </div>

    <div class="document">
        <template v-if="isViewingHistory">
            <DiffViewer
                v-if="comparisonArtefact?.blob && selectedArtefact?.blob"
                :from="comparisonArtefact.blob"
                :to="selectedArtefact.blob"
                viewType="history"
                :mimetype="selectedArtefact.mimetype"
                :diffFormat="
                    route.query.compare !== undefined
                        ? 'side-by-side'
                        : 'line-by-line'
                "
                :style="{ 'width': '100%' }"
            />
            <FileViewer
                v-else-if="selectedArtefact"
                :plainText="showAsPlainText"
                :artefact="selectedArtefact"
                :showToastOnSave="false"
            />
        </template>
        <template v-else>
            <div
                v-if="artefactMeta && contentFlavor === ContentFlavor.UNKNOWN"
                class="downloading-container"
            >
                <Button
                    label="Download"
                    icon="pi pi-download"
                    :loading="isDownloading"
                    @click="download"
                />
                <p>
                    MyStekz cannot render files of
                    {{ artefactMeta?.mimetype }} type.
                </p>
            </div>
            <div
                v-else-if="!isDoneLoading || downloadError"
                class="downloading-container"
            >
                <Spinner v-if="!downloadError" contained />
                <p>Downloading file...</p>
            </div>
            <FileViewer
                v-else-if="selectedArtefact"
                :plainText="showAsPlainText"
                :artefact="selectedArtefact"
                :showToastOnSave="false"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import DiffViewer from "@/components/Diff/DiffViewer.vue";
import EditableTitle from "@/components/EditableTitle.vue";
import FileViewer from "@/components/FileViewer.vue";
import { useEditManager, useEditStore } from "@/composables/edit-manager";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import {
    useArtefactManager,
    useArtefactsStore,
    withContent,
} from "@/lib/artefacts/user";
import { ContentFlavor, getMimetypeFlavor } from "@/lib/contentFlavor";
import Spinner from "@/shared/Spinner.vue";
import { useCurrentEntityManager } from "@/stores/entity-store";
import { useEventManager } from "@/stores/event-store";
import { useFlowManager } from "@/stores/flow-store";
import { useGatewayManager } from "@/stores/gateway-store";
import { useHistoryManager, useHistoryStore } from "@/stores/history";
import { useTaskManager } from "@/stores/task-store";
import { downloadBlob, mime } from "@/utils";
import { until } from "@vueuse/core";
import { getIcon } from "material-file-icons";
import Button, { ButtonProps } from "primevue/button";
import Menu from "primevue/menu";
import type { MenuItem } from "primevue/menuitem";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { computed, onUnmounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

const menu = ref<InstanceType<typeof Menu>>();
const fileUploadElement = ref<HTMLElement>();

const { artefact: artefactMeta } = useArtefactManager();
const { isEditing } = useEditManager();

const { loadHistory } = useHistoryStore();
const {
    isViewingLatestVersion,
    isViewingHistory,
    comparisonArtefact,
    isFetchingHistory,
    isFetchingHistoricalArtefact,
    selectedArtefact: selectedHistoricalArtefact,
} = useHistoryManager();

const router = useRouter();
const route = useRoute();
const { organization } = useOrganizationManager();
const { stek } = useStekManager();
const { task } = useTaskManager();
const { gateway } = useGatewayManager();
const { event } = useEventManager();
const { flow } = useFlowManager();
const { domain, product, process, role } = useCurrentEntityManager();

const forceDownload = ref(false);
const contentFlavor = computed(() => {
    return getMimetypeFlavor(artefactMeta.value?.mimetype);
});

const downloadCondition = computed(
    () => forceDownload.value || contentFlavor.value !== ContentFlavor.UNKNOWN
);

const { artefact, isFetching, downloadError, deleteFromCache } = withContent(
    artefactMeta,
    downloadCondition
);

const selectedArtefact = computed(() => {
    if (isViewingHistory.value) {
        return selectedHistoricalArtefact.value;
    }
    return artefact.value;
});

onUnmounted(() => {
    if (selectedArtefact.value) {
        deleteFromCache();
    }
});

const toast = useToast();

const isDownloading = ref(false);
const isDoneLoading = computed(() => {
    if (contentFlavor.value === ContentFlavor.UNKNOWN) {
        return true;
    }

    return (
        !isFetching.value &&
        !isFetchingHistory.value &&
        !isFetchingHistoricalArtefact.value &&
        !isRestoring.value &&
        !downloadError.value &&
        !isDownloading.value
    );
});

const artefactsStore = useArtefactsStore();
const isRestoring = ref(false);
const confirm = useConfirm();

const { startEditing } = useEditStore();

const { updateArtefacts } = useArtefactsStore();

const showAsPlainText = ref(false);

const download = async () => {
    isDownloading.value = true;
    forceDownload.value = true;
    try {
        await until(selectedArtefact).not.toBeUndefined();
        if (!selectedArtefact.value) {
            return;
        }
        const extension = mime.getExtension(selectedArtefact.value.mimetype);
        const blobUrl = URL.createObjectURL(selectedArtefact.value.blob);
        downloadBlob(blobUrl, `${selectedArtefact.value.name}.${extension}`);
    } finally {
        isDownloading.value = false;
    }
};

const uploadOverwrite = async (event: HTMLInputEvent) => {
    const file_ = event.target.files?.[0];
    if (!file_) {
        toast.add({
            severity: "error",
            detail: "No file selected.",
        });
        return;
    }
    if (!artefact.value?.mimetype.includes(file_.type)) {
        toast.add({
            severity: "warn",
            detail: "You can only overwrite the current file with the same file type.",
        });
        return;
    }
    await artefactsStore.updateArtefactContent(artefact.value, file_, {
        showToast: false,
    });
    artefact.value.updateContent(file_);
    artefact.value.persist();
    startEditing();
};

const deleteConfirmation = async () => {
    return new Promise((resolve) => {
        confirm.require({
            header: "Delete",
            message: "Are you sure you want to delete this file?",
            icon: "pi pi-exclamation-triangle",
            rejectProps: {
                label: "Cancel",
                severity: "secondary",
                outlined: true,
            },
            acceptProps: {
                label: "Delete",
                severity: "danger",
            },
            defaultFocus: "reject",
            accept: () => {
                resolve(true);
            },
            reject: () => {
                resolve(false);
            },
            onHide: () => {
                resolve(false);
            },
        });
    });
};

const removeFile = async () => {
    if (!artefactMeta.value) {
        return;
    }

    const shouldDelete = await deleteConfirmation();

    if (!shouldDelete) {
        return;
    }

    try {
        await artefactsStore.removeArtefact(artefactMeta.value.identity);
    } catch {
        toast.add({
            severity: "warn",
            detail: `Could not remove file ${artefactMeta.value?.name}`,
        });
        return;
    }

    await updateArtefacts();

    if (task.value) {
        router.push({
            name: "taskPage",
            params: {
                orgName: organization.value?.name,
                stekName: stek.value?.name,
                taskId: task.value.identity,
            },
        });
    } else if (gateway.value) {
        router.push({
            name: "gatewayPage",
            params: {
                orgName: organization.value?.name,
                stekName: stek.value?.name,
                gatewayId: gateway.value.identity,
            },
        });
    } else if (flow.value) {
        router.push({
            name: "flowPage",
            params: {
                orgName: organization.value?.name,
                stekName: stek.value?.name,
                flowId: flow.value.identity,
            },
        });
    } else if (event.value) {
        router.push({
            name: "eventPage",
            params: {
                orgName: organization.value?.name,
                stekName: stek.value?.name,
                eventId: event.value.identity,
            },
        });
    } else if (process.value) {
        router.push({
            name: "processPage",
            params: {
                orgName: organization.value?.name,
                stekName: stek.value?.name,
                processId: process.value.identity,
            },
        });
    } else if (product.value) {
        router.push({
            name: "productPage",
            params: {
                orgName: organization.value?.name,
                stekName: stek.value?.name,
                productId: product.value.identity,
            },
        });
    } else if (role.value) {
        router.push({
            name: "rolePage",
            params: {
                orgName: organization.value?.name,
                stekName: stek.value?.name,
                roleId: role.value.identity,
            },
        });
    } else if (domain.value) {
        router.push({
            name: "domainPage",
            params: {
                orgName: organization.value?.name,
                stekName: stek.value?.name,
                domainId: domain.value.identity,
            },
        });
    }
};
const restoreConfirmation = async () => {
    return new Promise((resolve) => {
        confirm.require({
            message: "Are you sure you want restore this version?",
            header: "Restore version",
            icon: "pi pi-exclamation-triangle",
            rejectProps: {
                label: "Cancel",
                severity: "secondary",
                outlined: true,
            },
            acceptProps: {
                label: "Restore",
            },
            accept: () => {
                resolve(true);
            },
            reject: () => {
                resolve(false);
            },
            onHide: () => {
                resolve(false);
            },
        });
    });
};
const buttonsAndTheirConditions = computed(() => [
    {
        class: "p-button-secondary",
        icon: "pi pi-check",
        label: "Restore",
        click: async () => {
            const shouldRestore = await restoreConfirmation();
            if (!shouldRestore) {
                return;
            }

            isRestoring.value = true;
            try {
                deleteFromCache();
                await artefactsStore.restoreArtefact(selectedArtefact.value!);
                await Promise.all([updateArtefacts(), loadHistory()]);
                await router.push({
                    name: route.name,
                    params: {
                        ...route.params,
                        version: "current",
                    },
                    query: route.query,
                });
            } finally {
                isRestoring.value = false;
            }
        },
        condition: () => !isViewingLatestVersion.value,
    },
]);

const buttons = computed<ButtonProps[]>(() => {
    return buttonsAndTheirConditions.value
        .filter((button) => !button.condition || button.condition())
        .map((button) => {
            return {
                class: button.class,
                icon: button.icon,
                label: button.label,
                onClick: button.click,
            };
        });
});

const toggleMenu = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    menu.value?.toggle(event);
};

const contextMenuItemsAndTheirConditions = computed(() => [
    {
        label: "Download",
        icon: "pi pi-download",
        command: download,
        condition: () => contentFlavor.value !== ContentFlavor.GOOGLEDRIVE,
    },
    {
        label: "Upload",
        icon: "pi pi-upload",
        command: () => fileUploadElement.value?.click(),
        condition: () =>
            contentFlavor.value !== ContentFlavor.GOOGLEDRIVE &&
            isViewingLatestVersion.value,
    },
    {
        label: "Open in Google",
        icon: "pi pi-google",
        command: () => {
            const content = JSON.parse(selectedArtefact.value?.content || "{}");
            if (content.url) {
                window.open(content.url, "_blank");
            }
        },
        condition: () => contentFlavor.value === ContentFlavor.GOOGLEDRIVE,
    },
    {
        label: "Copy source URL",
        icon: "pi pi-link",
        command: () => {
            const content = JSON.parse(selectedArtefact.value?.content || "{}");
            if (content.url) {
                navigator.clipboard.writeText(content.url);
                toast.add({
                    severity: "success",
                    summary: "URL copied to clipboard",
                });
            }
        },
        condition: () => contentFlavor.value === ContentFlavor.GOOGLEDRIVE,
    },
    {
        label: "Delete",
        icon: "pi pi-trash",
        command: removeFile,
        condition: () => isViewingLatestVersion.value,
    },
    {
        label: showAsPlainText.value
            ? "Show as rich text"
            : "Show as plain text",
        icon: "pi pi-align-justify",
        command: () => (showAsPlainText.value = !showAsPlainText.value),
        condition: () =>
            selectedArtefact.value?.mimetype.endsWith("markdown") &&
            !isViewingHistory.value,
    },
    {
        label: "History",
        icon: "pi pi-history",
        command: () => {
            router.push({
                name: route.name,
                params: {
                    ...route.params,
                    version: "current",
                },
            });
        },
        condition: () =>
            contentFlavor.value !== ContentFlavor.GOOGLEDRIVE &&
            !isViewingHistory.value &&
            !isEditing.value,
    },
]);

const contextMenuItems = computed<MenuItem[]>(() => {
    return contextMenuItemsAndTheirConditions.value
        .filter((item) => !item.condition || item.condition())
        .map((item) => {
            return {
                label: item.label,
                icon: item.icon,
                command: item.command,
            };
        });
});

const fileIcon = computed(() => {
    if (!artefactMeta.value) {
        return;
    }
    const filename = `.${mime.getExtension(artefactMeta.value.mimetype)}`;
    return getIcon(filename).svg;
});
</script>

<style scoped lang="scss">
.page-title {
    margin-bottom: 0;
    display: flex;
    gap: 0.5rem;

    .document-icon {
        height: 3rem;
    }
    .revision {
        display: block;
        margin-top: 1.6rem;
        margin-left: 0.5rem;
        line-height: 1.6em;
        transform: translateY(-50%);
    }
}

.controls {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding: 0.5rem 0;
    margin-bottom: 1rem;
}

.document {
    flex: 1;
    display: flex;

    .downloading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        min-height: 200px;

        .downloading-indicator {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 2rem;
            border-radius: 8px;
            background-color: rgba(var(--stekz-primary-rgb-color), 0.05);

            &.error {
                background-color: rgba(220, 53, 69, 0.05);
                border: 1px solid rgba(220, 53, 69, 0.1);

                .error-icon {
                    font-size: 2rem;
                    color: rgba(220, 53, 69, 0.8);
                    margin-bottom: 1rem;
                }
            }

            .download-info {
                margin-top: 1rem;
                text-align: center;
                color: var(--text-color-secondary);

                p {
                    margin: 0.25rem 0;
                }

                .download-mimetype {
                    font-weight: bold;
                    color: var(--text-color);
                    font-size: 0.9rem;
                }

                .error-message {
                    font-weight: bold;
                    color: rgba(220, 53, 69, 0.8);
                    font-size: 1rem;
                }

                .error-details {
                    font-size: 0.8rem;
                    max-width: 300px;
                    white-space: normal;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    margin-bottom: 1rem;
                }
            }
        }
    }
}
</style>
